import React, { Fragment, useState, useRef, useEffect } from "react";
import { flushSync } from "react-dom";
import { useNavigate, useLocation, Link } from "react-router-dom";
import generatePDF from 'react-to-pdf';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

import LoadPage from "../common/LoadPage";
import AlertMessage from "../common/AlertMessage";
import ImportRiskModal from "./ImportRiskModal";
import { replaceUnderscoresAndUppercase, getRiskSeverity, getScoreBorderClass, shouldSuggestAcceptTreatment } from "../common/helpers";

import useAxiosFetch from "../common/useAxiosFetch";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../AppContext";
import moment from "moment";
import { categoryIDtoLabel, getCategoryValue } from "../common/helpers";
import { MODAL_TYPES } from "../modals/ModalManager";

const DEFAULT_SORT_COLUMN = 'actual-score';
const DEFAULT_SORT_DIRECTION = 'desc';

const RiskPage = ({refreshData, setRefreshData}) => {
  const { currentOrg, currentProject, alertMessage, clearAlertMessage, openModal } = useAppContext();
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [sortColumn, setSortColumn] = useState(DEFAULT_SORT_COLUMN);
  const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);
  const [riskOwnerFilter, setRiskOwnerFilter] = useState('');
  const [ciaFilter, setCiaFilter] = useState('');
  const [riskScoreFilter, setRiskScoreFilter] = useState('');
  const [treatmentFilter, setTreatmentFilter] = useState('');
  const [riskIdsFilter, setRiskIdsFilter] = useState([]); // Subset of risk ids to show (e.g. from Risk Evaluation)
  const [showArchived, setShowArchived] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [visibleItems, setVisibleItems] = useState({});
  const [isHeaderStuck, setIsHeaderStuck] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [pdfExportedAt, setPdfExportedAt] = useState(null);
  const exportRef = useRef();
  const headerRef = useRef();

  const navigate = useNavigate();
  // Archived risks are always fetched so the page knows they exist, and are hidden client-side unless requested.
  const { data, loading, refetch } = useAxiosFetch("/api/risks/" + (currentProject.id ? "byProject/" + currentProject.id : "byOrg/" + currentOrg.id) + "?includeArchived=true");
  const { data: riskTemplates, loading: loadingRiskTemplates } = useAxiosFetch("/api/riskTemplates/securityStandard/" + currentOrg.securityStandard || "ISO27001");
  const { data: featureLimits } = useAxiosFetch("/api/features/limits");

  //forces a refresh when risk is deleted/updated score/etc
  useEffect(() => {
    if(refreshData > 0)
      refetch();
  }, [refreshData]); // Refetch data whenever refreshData changes.

  // Set filters from state or URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const riskIdsParam = params.get('riskIds');
    setRiskOwnerFilter(params.get('riskOwnerFilter') || '');
    setCiaFilter(params.get('ciaFilter') || '');
    setRiskScoreFilter(params.get('riskScoreFilter') || '');
    setTreatmentFilter(params.get('treatmentFilter') || '');
    setShowFilters(params.get('showFilters') === 'true');
    setShowArchived(params.get('showArchived') === 'true');
    setRiskIdsFilter(riskIdsParam ? riskIdsParam.split(',').map(id => id.trim()).filter(Boolean) : []);
    setSortColumn(params.has('sortColumn') ? params.get('sortColumn') : DEFAULT_SORT_COLUMN);
    setSortDirection(params.get('sortDirection') === 'asc' || params.get('sortDirection') === 'desc' ? params.get('sortDirection') : DEFAULT_SORT_DIRECTION);
  }, [location.search]);

  // The empty dashboard links here with { startAnalysis: true } to open the risk analysis straight away
  useEffect(() => {
    if (!location.state?.startAnalysis || loading || loadingRiskTemplates) return;
    navigate(location.pathname + location.search, { replace: true, state: null });
    if (riskTemplates?.length > 1) {
      setShowTemplateModal(true);
    } else {
      clearAlertMessage();
      openModal(MODAL_TYPES.RISK, riskTemplates?.length === 1 ? { riskObj: riskTemplates[0].risks } : undefined);
    }
  }, [location.state, loading, loadingRiskTemplates]);

  // add border bottom to table head when scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (headerRef.current) {
        setIsHeaderStuck(headerRef.current.getBoundingClientRect().bottom <= 140);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  if (loading || loadingRiskTemplates) return <LoadPage />;

  const uniqueRiskOwners = [...new Set(data.filter(item => showArchived || !item.archived).map(item => item.owner))];

  // Aggregate stats for quick glance and filtering
  // Archived risks are only listed for the audit trail; they never count towards the stats.
  const activeData = data.filter(item => !item.archived);
  const statsData = riskIdsFilter.length ? activeData.filter(item => riskIdsFilter.includes(item.id)) : activeData;
  const stats = statsData.reduce((acc, item) => {
    const score = item["actual-chance"] * item['actual-impact'];
    acc.count += 1;
    acc.total += score;
    acc[getRiskSeverity(score, currentOrg.rangeBounds)] += 1;
    return acc;
  }, { count: 0, total: 0, low: 0, medium: 0, high: 0 });
  const avgScore = stats.count ? Math.round((stats.total / stats.count) * 100) / 100 : 0;

  // Handle starting risk analysis for new users
  const handleStartRiskAnalysis = () => {
    if (riskTemplates && riskTemplates.length === 1) {
      // If only one template exists, directly open the batch import with that template
      clearAlertMessage();
      openModal(MODAL_TYPES.RISK, { riskObj: riskTemplates[0].risks });
    } else if (riskTemplates && riskTemplates.length > 1) {
      // If multiple templates exist, show the selection modal
      setShowTemplateModal(true);
    } else {
      // No templates available, just open the add risk modal
      clearAlertMessage();
      openModal(MODAL_TYPES.RISK);
    }
  };

  const filteredData = data.filter(item => {
    if (riskIdsFilter.length && !riskIdsFilter.includes(item.id)) return false;

    if (item.archived && !showArchived) return false;

    if (riskOwnerFilter && item.owner !== riskOwnerFilter) return false;

    switch (ciaFilter) {
        case 'confidentiality':
            if (!item.confidentiality) return false;
            break;
        case 'availability':
            if (!item.availability) return false;
            break;
        case 'integrity':
            if (!item.integrity) return false;
            break;
        default:
            break;
    }

    switch (riskScoreFilter) {
      case 'low':
        if (getRiskSeverity(item["actual-chance"]*item['actual-impact'], currentOrg.rangeBounds) !== 'low') return false;
        break;
      case 'medium':
        if (getRiskSeverity(item["actual-chance"]*item['actual-impact'], currentOrg.rangeBounds) !== 'medium') return false;
        break;
      case 'high':
        if (getRiskSeverity(item["actual-chance"]*item['actual-impact'], currentOrg.rangeBounds) !== 'high') return false;
        break;
    }

    if (treatmentFilter) {
      if (treatmentFilter === 'reduce' && item.treatment !== 'reduce') return false;
      if (treatmentFilter === 'accept' && item.treatment !== 'accept') return false;
      if (treatmentFilter === 'avoid' && item.treatment !== 'avoid') return false;
      if (treatmentFilter === 'transfer' && item.treatment !== 'transfer') return false;
    }



    return true;
  });

  // Sorting function
  const sortedData = [...filteredData].sort((a, b) => {
    let comparison = 0;
    switch (sortColumn) {
      case 'description': comparison = a.description.localeCompare(b.description); break;
      case 'measures': comparison = a.measures.length - b.measures.length; break;
      case 'treatment': comparison = a.treatment?.localeCompare(b.treatment); break;
      case 'initial-score': comparison = a['initial-chance']*a['initial-impact'] - b['initial-chance']*b['initial-impact']; break;
      case 'actual-score': comparison = a['actual-chance']*a['actual-impact'] - b['actual-chance']*b['actual-impact']; break;
      case 'expected-score': comparison = a['expected-chance']*a['expected-impact'] - b['expected-chance']*b['expected-impact']; break;  
      case 'owner': comparison = a.owner.localeCompare(b.owner); break;
      case 'updatedAt': comparison = (a.updatedAt || a.createdAt || a._ts) - (b.updatedAt || b.createdAt || b._ts); break; // New sort option

      default: break;
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const formatExportTimestamp = (value) => moment(value).format('YYYY-MM-DD HH:mm');
  const getExportFilename = (exportedAt, extension) =>
    currentOrg.name.toLowerCase() + '_risk_export_' + moment(exportedAt).format('YYYY-MM-DD') + '.' + extension;

  const updateSort = (column, direction) => {
    setSortColumn(column);
    setSortDirection(direction);

    const params = new URLSearchParams(location.search);
    params.set('sortColumn', column);
    params.set('sortDirection', direction);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleSort = column => {
    if (sortColumn === column) {
      updateSort(column, sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      updateSort(column, 'asc');
    }
  };

  const toggleVisibility = (id) => {
    setVisibleItems(prevState => ({
      ...prevState,
      [id]: !prevState[id]
    }));
  };

  // Update URL with current filter state
  const updateURLWithFilters = () => {
    const params = new URLSearchParams();
    if (riskOwnerFilter) params.set('riskOwnerFilter', riskOwnerFilter);
    if (ciaFilter) params.set('ciaFilter', ciaFilter);
    if (riskScoreFilter) params.set('riskScoreFilter', riskScoreFilter);
    if (treatmentFilter) params.set('treatmentFilter', treatmentFilter);
    if (showFilters) params.set('showFilters', 'true');
    params.set('sortColumn', sortColumn);
    params.set('sortDirection', sortDirection);

    navigate(`?${params.toString()}`, { replace: true });
  };

  const toggleShowFilters = () => {
    const newShowFilters = !showFilters;
    setShowFilters(newShowFilters);
    
    // Update URL immediately
    const params = new URLSearchParams(location.search);
    if (newShowFilters) {
      params.set('showFilters', 'true');
    } else {
      params.delete('showFilters');
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  // Filter change handlers that also update URL
  const handleRiskOwnerFilterChange = (value) => {
    setRiskOwnerFilter(value);
    updateURLWithFilterValue('riskOwnerFilter', value);
  };

  const handleCiaFilterChange = (value) => {
    setCiaFilter(value);
    updateURLWithFilterValue('ciaFilter', value);
  };

  const handleRiskScoreFilterChange = (value) => {
    setRiskScoreFilter(value);
    updateURLWithFilterValue('riskScoreFilter', value);
  };

  const handleTreatmentFilterChange = (value) => {
    setTreatmentFilter(value);
    updateURLWithFilterValue('treatmentFilter', value);
  };

  const handleShowArchivedChange = (value) => {
    setShowArchived(value);
    updateURLWithFilterValue('showArchived', value ? 'true' : '');
  };



  const updateURLWithFilterValue = (filterKey, filterValue) => {
    const params = new URLSearchParams(location.search);
    if (filterValue) {
      params.set(filterKey, filterValue);
    } else {
      params.delete(filterKey);
    }
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleExport = (exportType) => {
    // Check if environment is on trial plan (no paid subscription)
    const planId = featureLimits?.planInfo?.planId;
    const isTrialPlan = planId === 'trial';
    
    if (isTrialPlan) {
      openModal(MODAL_TYPES.UPGRADE, { featureType: 'Export' });
      setShowExportMenu(false);
      return;
    }

    // Proceed with export
    if (exportType === 'pdf') {
      exportTableToPdf();
    } else if (exportType === 'excel') {
      exportTableToExcel();
    }
    setShowExportMenu(false);
  };

  const exportTableToPdf = async () => {
    const exportedAt = new Date();
    // Render the summary caption synchronously so it is part of the captured table
    flushSync(() => setPdfExportedAt(exportedAt));
    try {
      await generatePDF(exportRef, { filename: getExportFilename(exportedAt, 'pdf'), page: { orientation: 'landscape' } });
    } finally {
      setPdfExportedAt(null);
    }
  };

  const addSummaryWorksheet = (workbook, exportedAt) => {
    const summarySheet = workbook.addWorksheet(t('risk_summary'));
    summarySheet.columns = [
      { key: 'label', width: 25 },
      { key: 'value', width: 15 },
    ];

    summarySheet.addRow({ label: t('risk_summary') }).font = { bold: true, size: 14 };
    summarySheet.addRow({ label: currentOrg.name });
    summarySheet.addRow({ label: t('exported_on'), value: formatExportTimestamp(exportedAt) });
    summarySheet.addRow({});

    const severityFills = {
      low: 'FF4CAF50',
      medium: 'FFFFC107',
      high: 'FFF44336',
    };
    ['low', 'medium', 'high'].forEach(severity => {
      const row = summarySheet.addRow({ label: t(severity), value: stats[severity] });
      row.getCell('label').border = { left: { style: 'thick', color: { argb: severityFills[severity] } } };
    });
    summarySheet.addRow({ label: t('average') + ' ' + t('score'), value: avgScore });
    summarySheet.addRow({ label: t('total'), value: stats.count }).font = { bold: true };
  };

  const exportTableToExcel = async () => {
    const exportedAt = new Date();
    // Create a new workbook and worksheet
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('risk_export'), {
      views: [{ state: 'frozen', xSplit: 0, ySplit: 1 }]
    });

    // Define the border style
    const borderStyles = {
      top: { style: 'thick', size: 1 },
      left: { style: 'thick', size: 1 },
      bottom: { style: 'thick', size: 1 },
      right: { style: 'thick', size: 1 }
    };

    // Set up the headers - assuming row 7 is the beginning of your table 
    const headerRow = worksheet.getRow(1);

    // Define columns and their headers with styling
    let worksheet_columns = [
      { key: 'description', width: 60 },
      { key: 'owner', width: 30 },
      { key: 'confidentiality', width: 7 },
      { key: 'integrity', width: 7 },
      { key: 'availability', width: 7 },
      { key: 'initial-chance', width: 15 },
      { key: 'initial-impact', width: 15 },
      { key: 'initial-score', width: 15 },
      { key: 'actual-chance', width: 15 },
      { key: 'actual-impact', width: 15 },
      { key: 'actual-score', width: 15 },
      { key: 'expected-chance', width: 15 },
      { key: 'expected-impact', width: 15 },
      { key: 'expected-score', width: 15 },
      { key: 'treatment', width: 20 },
      { key: 'treatmentHow', width: 20 },
      { key: 'temporarily_accept', width: 20 },
      { key: 'until_when', width: 20 },
      { key: 'approval_status', width: 20 },
    ];

    // find the risk with the most measures and add that many columns
    let maxMeasures = 0;
    sortedData.forEach((item) => {
      if (item.measures.length > maxMeasures) {
        maxMeasures = item.measures.length;
      }
    });

    for (let i = 0; i < maxMeasures; i++) {
      worksheet_columns.push({ key: 'measure' + i, width: 30 });
      worksheet_columns.push({ key: 'category' + i, width: 30 });
      worksheet_columns.push({ key: 'status' + i, width: 30 });
    }

    worksheet.columns = worksheet_columns;

    headerRow.getCell(1).value = t('risk_description');
    headerRow.getCell(2).value = t('owner');
    headerRow.getCell(3).value = 'C';
    headerRow.getCell(4).value = 'I';
    headerRow.getCell(5).value = 'A';
    headerRow.getCell(6).value = t('initial') + ' ' + t('chance');
    headerRow.getCell(7).value = t('initial') + ' ' + t('impact');
    headerRow.getCell(8).value = t('initial') + ' ' + t('score');
    headerRow.getCell(9).value = t('current') + ' ' + t('chance');
    headerRow.getCell(10).value = t('current') + ' ' + t('impact');
    headerRow.getCell(11).value = t('current') + ' ' + t('score');
    headerRow.getCell(12).value = t('target') + ' ' + t('chance');
    headerRow.getCell(13).value = t('target') + ' ' + t('impact');
    headerRow.getCell(14).value = t('target') + ' ' + t('score');
    headerRow.getCell(15).value = t('treatment');
    headerRow.getCell(16).value = t('how') + '?';
    headerRow.getCell(17).value = t('temporarily_accept');
    headerRow.getCell(18).value = t('until_when');
    headerRow.getCell(19).value = t('approval_status');

    // Add the measure headers
    for (let i = 0; i < maxMeasures; i++) {
      headerRow.getCell(20 + i * 3).value = t('measure') + ' ' + (i + 1);
      headerRow.getCell(21 + i * 3).value = (currentOrg.securityStandard || 'ISO27001') + ' ' + t('category') + ' ' + (i + 1);
      headerRow.getCell(22 + i * 3).value = t('measure') + ' ' + t('status') + ' ' + (i + 1);
    }

    // Apply bold style to headers
    headerRow.eachCell(cell => {
      cell.font = { bold: true };
    });

    // Define the range for the filter (covering all headers)
    const lastColumnLetter = worksheet.columns[worksheet.columns.length - 1].letter;
    const filterRange = `A1:${lastColumnLetter}7`;
    worksheet.autoFilter = filterRange;

    // Populate the worksheet with data
    sortedData.map((item) => {
      let row = {
        description: item.description,
        owner: item.owner,
        confidentiality: item.confidentiality ? 'X' : '',
        integrity: item.integrity ? 'X' : '',
        availability: item.availability ? 'X' : '',
        'initial-chance': parseInt(item['initial-chance']),
        'initial-impact': parseInt(item['initial-impact']),
        'initial-score': parseInt(item['initial-chance']) * parseInt(item['initial-impact']),
        'actual-chance': parseInt(item['actual-chance']),
        'actual-impact': parseInt(item['actual-impact']),
        'actual-score': parseInt(item['actual-chance']) * parseInt(item['actual-impact']),
        'expected-chance': parseInt(item['expected-chance']),
        'expected-impact': parseInt(item['expected-impact']),
        'expected-score': parseInt(item['expected-chance']) * parseInt(item['expected-impact']),
        treatment: item.treatment ? t(item.treatment) : '',
        treatmentHow: item.treatmentHow,
        temporarily_accept: item.tempAcceptTreatment ? t('yes') : '',
        until_when: item.acceptTreatmentDate,
        approval_status: item.riskApproved === true ? t('approved') : item.riskApproved === false ? t('rejected') : t('pending'),
      };

      // Add the measures
      item.measures.forEach((measure, i) => {
        row['measure' + i] = measure.measure;
        row['category' + i] = measure.category?.map(cat => getCategoryValue(cat)).join(', ');
        row['status' + i] = measure.status ? t(measure.status) : '';
      });

      worksheet.addRow(row);
    });

    // Added after the risk sheet so the risks stay the first worksheet (used by the import)
    addSummaryWorksheet(workbook, exportedAt);

    // Write to a buffer and save
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, getExportFilename(exportedAt, 'xlsx'));
  };


  // Show empty state for new users
  if (data.length === 0) {
    return (
      <div id="content">


        <div className="risk-empty">
          <div className="risk-empty-icon">
            <i className="icon-Risico" />
          </div>
          
          <h2>{t('no_risks_yet')}</h2>
          
          <p className="risk-empty-lead">
            {riskTemplates && riskTemplates.length > 0 
              ? t('start_with_template_description')
              : t('start_by_adding_first_risk')}
          </p>

          <div className="risk-empty-actions">
            <button type="button" className="rq-btn rq-btn-primary rq-btn-lg" onClick={handleStartRiskAnalysis}>
              <i className="icon-play" />
              {riskTemplates && riskTemplates.length > 0 
                ? t('start_risk_analysis')
                : t('add_first_risk')}
            </button>

            {riskTemplates && riskTemplates.length > 0 && (
              <button
                type="button"
                className="rq-btn rq-btn-secondary rq-btn-lg"
                onClick={() => { clearAlertMessage(); openModal(MODAL_TYPES.RISK); }}
              >
                <i className="icon-add" />
                {t('add_risk_manually')}
              </button>
            )}
          </div>

          {riskTemplates && riskTemplates.length > 0 && (
            <div className="risk-empty-tip">
              <i className="icon-idea" />
              <div>
                <h4>{t('tip')}</h4>
                <p>
                  {riskTemplates.length === 1
                    ? t('one_template_available_tip')
                    : t('multiple_templates_available_tip', { count: riskTemplates.length })}
                </p>
              </div>
            </div>
          )}
        </div>

        <ImportRiskModal
          show={showTemplateModal}
          handleClose={() => setShowTemplateModal(false)}
          riskTemplates={riskTemplates}
        />
      </div>
    );
  }

  return (
    <div id="content">
      {/* Curated-subset banner: shown when arriving from Risk Evaluation */}
      {riskIdsFilter.length > 0 && (
        <div
          className="content-bubble"
          style={{
            // .content-bubble floats; a floated full-width banner pushes the flex toolbar off-screen
            float: 'none',
            boxSizing: 'border-box',
            marginTop: 0,
            padding: 16,
            marginBottom: 16,
            borderLeft: '4px solid var(--yellow-risk-score, #f39c12)',
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <i className="icon-evaluation" style={{ fontSize: '1.2em', opacity: 0.8 }} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 600 }}>{t('rep_risks_banner_title', 'Risks selected for evaluation')}</div>
            <div style={{ opacity: 0.8, fontSize: '0.9em' }}>
              {t('rep_risks_banner_text', 'Showing only the risks selected for re-evaluation. Update the score of each risk below.')}
            </div>
          </div>
          <div className="white-border-btn" style={{ marginLeft: 'auto' }} onClick={() => navigate('/risks')}>
            <i className="icon-close" />&nbsp;{t('rep_risks_banner_clear', 'Show all risks')}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="content-header" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div className={`white-border-btn ${showFilters ? 'active' : ''}`} style={{marginLeft: 0}} onClick={toggleShowFilters}>
          <i className="icon-filter" />&nbsp;Filters
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <div className="white-border-btn" onClick={() => { clearAlertMessage(); openModal(MODAL_TYPES.RISK); }}>+ {t('add_risk_short')}</div>
          <div className="white-border-btn" onClick={() => setShowTemplateModal(true)}><i className="icon-import" />&nbsp;{t('import')}</div>
          <div style={{ position: 'relative' }}>
            <div className="white-border-btn" onClick={() => setShowExportMenu(!showExportMenu)}>
              <i className="icon-download" />&nbsp;{t('excel_export')}
            </div>
            {showExportMenu && (
              <div className="dropdown-menu show" style={{
                position: 'absolute',
                top: '100%',
                borderRadius: 3,
                right: 0,
                marginTop: 4,
                border: '2px solid var(--text-color)',
                zIndex: 1000,
                minWidth: 120
              }}>
                <div
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                  onClick={() => handleExport('excel')}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--light-blue-bg-color)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <i className="icon-file-excel" style={{ color: 'var(--primary-color)' }} />
                  Excel
                </div>
                <div
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8
                  }}
                  onClick={() => handleExport('pdf')}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--light-blue-bg-color)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                >
                  <i className="icon-file-pdf" style={{ color: 'var(--primary-color)' }} />
                  PDF
                </div>
              </div>
            )}
          </div>
        </div>

        <ImportRiskModal
          show={showTemplateModal}
          handleClose={() => setShowTemplateModal(false)}
          riskTemplates={riskTemplates}
        />
      </div>

      {/* Collapsible Filters */}
      {showFilters && (
        <div className="content-bubble" style={{ padding: 16, marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <select
              className="blue-dropdown"
              value={riskOwnerFilter}
              style={{ width: 200 }}
              onChange={e => handleRiskOwnerFilterChange(e.target.value)}
            >
              <option value="">{t('owner_without_prefix')} filter</option>
              {uniqueRiskOwners.map(owner => (
                <option key={owner} value={owner}>
                  {owner}
                </option>
              ))}
            </select>

            <select className="blue-dropdown" style={{ width: 180 }} value={ciaFilter} onChange={(e) => handleCiaFilterChange(e.target.value)}>
              <option value="">{t('cia').toUpperCase()} filter</option>
              <option value="confidentiality">{t('confidentiality')}</option>
              <option value="availability">{t('availability')}</option>
              <option value="integrity">{t('integrity')}</option>
            </select>

            <select className="blue-dropdown" value={riskScoreFilter} onChange={(e) => handleRiskScoreFilterChange(e.target.value)}>
              <option value="">Score filter</option>
              <option value="low">{t('low')}</option>
              <option value="medium">{t('medium')}</option>
              <option value="high">{t('high')}</option>
            </select>

            <select className="blue-dropdown" value={treatmentFilter} onChange={(e) => handleTreatmentFilterChange(e.target.value)}>
              <option value="">{t('treatment')} filter</option>
              <option value="reduce">{t('reduce')}</option>
              <option value="accept">{t('accept')}</option>
              <option value="avoid">{t('avoid')}</option>
              <option value="transfer">{t('transfer')}</option>
            </select>

            <label className="blue-checkbox-filter">
              <input type="checkbox" checked={showArchived} onChange={(e) => handleShowArchivedChange(e.target.checked)} />
              {t('show_archived_risks')}
            </label>

            <select className="blue-dropdown sort-risks-header" value={sortColumn} onChange={(e) => {
              const selectedColumn = e.target.value;
              updateSort(selectedColumn, selectedColumn === 'updatedAt' || selectedColumn === 'measures' || selectedColumn.indexOf('score') !== -1 ? 'desc' : 'asc');
            }}>
              <option value="">↑↓ Sort by</option>
              {['updatedAt', 'description', 'measures', 'treatment', 'initial-score', 'actual-score', 'expected-score', 'owner'].map(column => (
                <option key={column} value={column}>
                  {t(column)} {sortColumn === column && (sortDirection === 'asc' ? '↑' : '↓')}
                </option>
              ))}
            </select>

            {sortColumn && (
              <div
                className="blue-dropdown"
                style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, width: 'fit-content' }}
                onClick={() => updateSort(sortColumn, sortDirection === 'asc' ? 'desc' : 'asc')}
                title={t('toggle_sort_direction')}
              >
                <i className={`icon-arrow-${sortDirection === 'asc' ? 'up' : 'down'}`} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Stats summary */}
      <div className="content-bubble" style={{ padding: 16 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 700 }}>{t('risk')} {t('overview')}</div>
          <div 
            className={`score-border-green score-chip ${riskScoreFilter === 'low' ? 'score-chip-active' : ''}`} 
            style={{ padding: '4px 10px', cursor: 'pointer', background: riskScoreFilter === 'low' ? 'var(--green-risk-score)' : 'transparent', color: riskScoreFilter === 'low' ? 'white' : 'inherit' }} 
            onClick={() => handleRiskScoreFilterChange(riskScoreFilter === 'low' ? '' : 'low')}
          >
            {t('low')}: {stats.low}
          </div>
          <div 
            className={`score-border-yellow score-chip ${riskScoreFilter === 'medium' ? 'score-chip-active' : ''}`} 
            style={{ padding: '4px 10px', cursor: 'pointer', background: riskScoreFilter === 'medium' ? 'var(--yellow-risk-score)' : 'transparent', color: riskScoreFilter === 'medium' ? 'white' : 'inherit' }} 
            onClick={() => handleRiskScoreFilterChange(riskScoreFilter === 'medium' ? '' : 'medium')}
          >
            {t('medium')}: {stats.medium}
          </div>
          <div 
            className={`score-border-red score-chip ${riskScoreFilter === 'high' ? 'score-chip-active' : ''}`} 
            style={{ padding: '4px 10px', cursor: 'pointer', background: riskScoreFilter === 'high' ? 'var(--red-risk-score)' : 'transparent', color: riskScoreFilter === 'high' ? 'white' : 'inherit' }} 
            onClick={() => handleRiskScoreFilterChange(riskScoreFilter === 'high' ? '' : 'high')}
          >
            {t('high')}: {stats.high}
          </div>
          <div style={{ marginLeft: 'auto', opacity: 0.8 }}>
            {t('average')} {t('score')}: <b>{avgScore}</b> • {t('total')}: <b>{stats.count}</b>
          </div>
        </div>
      </div>

      <div className="clear-both" />

      <AlertMessage type={alertMessage.type} message={alertMessage.content} />

      <div className="content-bubble content-bubble-table">
          <div className="table-wrapper">
            <table ref={exportRef} className="overview-table risk-overview-table">
              {pdfExportedAt && (
                <caption>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 16px 0', textAlign: 'left' }}>
                    <div style={{ fontWeight: 700 }}>{currentOrg.name}</div>
                    <div style={{ opacity: 0.8 }}>{t('exported_on')}: <b>{formatExportTimestamp(pdfExportedAt)}</b></div>
                  </div>
                  <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', padding: 16 }}>
                    <div style={{ fontWeight: 700 }}>{t('risk')} {t('overview')}</div>
                    <div className="score-border-green score-chip" style={{ padding: '4px 10px' }}>{t('low')}: {stats.low}</div>
                    <div className="score-border-yellow score-chip" style={{ padding: '4px 10px' }}>{t('medium')}: {stats.medium}</div>
                    <div className="score-border-red score-chip" style={{ padding: '4px 10px' }}>{t('high')}: {stats.high}</div>
                    <div style={{ marginLeft: 'auto', opacity: 0.8 }}>
                      {t('average')} {t('score')}: <b>{avgScore}</b> • {t('total')}: <b>{stats.count}</b>
                    </div>
                  </div>
                </caption>
              )}
              <thead className={`sticky-header ${isHeaderStuck ? 'stuck-header' : ''}`} ref={headerRef}>
                <tr>
                  <td className="align-left pointer-class" onClick={() => handleSort('description')}>
                    {t('risk_description')} {sortColumn === 'description' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </td>
                  <td className="align-left">{t('cia').toUpperCase()}</td>
                  <td className="pointer-class" onClick={() => handleSort('measures')}>{t('measures')}{sortColumn === 'measures' && (sortDirection === 'asc' ? '↑' : '↓')}</td>
                  <td colSpan="3">Score</td>
                  <td className="pointer-class" onClick={() => handleSort('treatment')}>{t('treatment')}{sortColumn === 'treatment' && (sortDirection === 'asc' ? '↑' : '↓')}</td>
                  <td className="pointer-class" onClick={() => handleSort('owner')}>{t('risk_owner')}{sortColumn === 'owner' && (sortDirection === 'asc' ? '↑' : '↓')}</td>
                  <td width="150">{t('actions')}</td>
                </tr>
                <tr>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td onClick={() => handleSort('initial-score')} className="pointer-class initial-table-header">
                    <div>{t('initial')}{sortColumn === 'initial-score' && (sortDirection === 'asc' ? '↑' : '↓')}</div>
                  </td>
                  <td onClick={() => handleSort('actual-score')} className="pointer-class current-table-header">
                    <div>{t('current')}{sortColumn === 'actual-score' && (sortDirection === 'asc' ? '↑' : '↓')}</div>
                  </td>
                  <td onClick={() => handleSort('expected-score')} className="pointer-class expected-table-header">
                    <div>{t('target')}{sortColumn === 'expected-score' && (sortDirection === 'asc' ? '↑' : '↓')}</div>
                  </td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                  <td>&nbsp;</td>
                </tr>
              </thead>
              <tbody>
                {sortedData.map((item) => {
                  return (
                    <Fragment key={item.id}>
                      <tr style={item.archived ? { opacity: 0.55 } : undefined}>
                        <td className="pointer-class align-left">
                          <Link to={`/viewrisk?riskId=${item.id}`} className="risk-table-link">
                            {item.description}
                          </Link>
                          {item.archived && (
                            <div className="small-text" title={item.archiveReason} style={{ marginTop: 4, fontWeight: 600 }}>
                              <i className="icon-archive" /> {t('archived')}
                            </div>
                          )}
                        </td>
                        <td className="align-left">
                          {item.confidentiality ? <div className="cia-text">{t('confidentiality')}</div> : ''}
                          {item.availability ? <div className="cia-text">{t('availability')}</div> : ''}
                          {item.integrity ? <div className="cia-text">{t('integrity')}</div> : ''}
                        </td>
                        <td
                          onClick={() => (item.measures?.length > 0 ? toggleVisibility(item.id) : null)}
                          className={"measure-column " + (item.measures?.length > 0 ? 'pointer-class' : '')}
                        >
                          {item.measures?.length ?? 0} {(item.measures?.length ?? 0) > 0 ? (visibleItems[item.id] ? '▲' : '▼') : ''}
                        </td>
                        <td className="initial-table-column">{item['initial-chance'] * item['initial-impact'] || '-'}</td>
                        <td
                          onClick={item.archived ? undefined : () => openModal(MODAL_TYPES.UPDATE_RISK_SCORE, { riskObj: data.find((risk) => risk.id === item.id) })}
                          className={"current-table-column" + (item.archived ? "" : " pointer-class")}
                        >
                          <div className={getScoreBorderClass(item['actual-chance'] * item['actual-impact'], currentOrg.rangeBounds)}
                          >
                            {item['actual-chance'] * item['actual-impact'] || '-'}
                          </div>
                        </td>
                        <td className="expected-table-column">{item['expected-chance'] * item['expected-impact'] || '-'}</td>
                        <td>
                          {replaceUnderscoresAndUppercase(item.treatment ? t(item.treatment) : '')}
                          {!item.archived && shouldSuggestAcceptTreatment(item) && (
                            <div
                              className="pointer-class"
                              title={t('treatment_suggestion_text')}
                              onClick={() => openModal(MODAL_TYPES.UPDATE_RISK_SCORE, { riskObj: data.find((risk) => risk.id === item.id) })}
                              style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4, color: 'var(--green-risk-score)', fontSize: 12, fontWeight: 600 }}
                            >
                              <i className="icon-Risico" />
                              <span>{t('treatment_suggestion_badge')}</span>
                            </div>
                          )}
                          {item.treatment === 'accept' && item.tempAcceptTreatment === true && item.acceptTreatmentDate && (
                            <>
                              <br />
                              <span className="small-text">{t('until')}: {moment(item.acceptTreatmentDate).format('DD-MM-YYYY')}</span>
                            </>
                          )}
                        </td>
                        <td>{item.owner}</td>
                        <td>
                          {item.archived ? (
                            <Link to={`/viewrisk?riskId=${item.id}`} title={t('archived')}>
                              <i className="icon-view" />
                            </Link>
                          ) : (
                            <>
                              <i
                                onClick={() => openModal(MODAL_TYPES.UPDATE_RISK_SCORE, { riskObj: data.find((risk) => risk.id === item.id) })}
                                className="icon-score"
                              />
                              <i
                                onClick={() => openModal(MODAL_TYPES.RISK, { riskObj: data.find((risk) => risk.id === item.id) })}
                                className="icon-edit"
                              />
                            </>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td
                          colSpan="9"
                          style={{ display: visibleItems[item.id] ? '' : 'none' }}
                          className="hidden-row"
                          id={'measures-' + item.id}
                        >
                          <table className="table-details">
                            <thead>
                              <tr>
                                <td>{t('measure')}</td>
                                <td>{t('category')}</td>
                                <td>{t('status')}</td>
                                <td>{t('owner_without_prefix')}</td>
                                <td>Deadline {t('date')}</td>
                                <td>{t('link_to_actions')}</td>
                              </tr>
                            </thead>
                            <tbody>
                              {item.measures?.map((measure, mIndex) => {
                                return (
                                  <tr key={mIndex}>
                                    <td>{measure.measure}</td>
                                    <td>
                                      {measure.category && measure.category.map((cat, cIndex) => (
                                        <div key={cIndex}>{categoryIDtoLabel(getCategoryValue(cat), i18n.language, currentOrg.securityStandard)}</div>
                                      ))}
                                    </td>
                                    <td>{replaceUnderscoresAndUppercase(measure.status ? t(measure.status) : '')}</td>
                                    <td>{measure.owner}</td>
                                    <td>{measure.deadlineDate ? moment(measure.deadlineDate).format('DD-MM-YYYY') : ''}</td>
                                    <td>
                                      {measure.action && measure.action.startsWith('http') ? (
                                        <a href={measure.action} className="action-link" target="_blank" rel="noopener noreferrer">{t('go_to_action')}</a>
                                      ) : (
                                        measure.action
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                              <tr />
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
    </div>
  );
};

export default RiskPage;
